import Button from 'ext-ui/components/Button/Button'
import React, { useEffect, useMemo, useRef, useState } from 'react'
import type { ReportingChartQuestionContext } from 'src/agent/reporting/chartQuestion.types'
import type {
  ReportingArtifact,
  ReportingChatMessage,
} from 'src/agent/reporting/reportingPresentation.types'
import { useLoopingTypingEffect } from 'src/hooks'
import { useTranslate } from 'src/localization/Trns'

import ChartQuestionContextCard from '../ChartQuestionContextCard/ChartQuestionContextCard'
import ReportingArtifactCard from '../ReportingArtifactCard/ReportingArtifactCard'
import {
  ChatBox,
  ChatError,
  ChatMarkdown,
  ChatStatus,
  ChatToggle,
  ChatWrapper,
  InputBox,
  LoadingText,
  MessageBox,
  MessageHistoryControl,
  MessageWrapper,
  Timestamp,
} from './ReportingAgentChat.style'
import ReportingAgentChatEmptyState from './ReportingAgentChatEmptyState'
import ReportingAgentChatField from './ReportingAgentChatField'

export type ReportingChatRunState =
  | 'idle'
  | 'submitting'
  | 'running'
  | 'refining'
  | 'error'

export interface ReportingAgentChatProps {
  messages?: readonly ReportingChatMessage[]
  chartQuestionContext?: ReportingChartQuestionContext
  artifactByRunId?: ReadonlyMap<string, ReportingArtifact>
  value?: string
  onDraftChange?: (value: string) => void
  onSubmit?: (value: string) => void
  statusMessage?: string
  errorMessage?: string
  runState?: ReportingChatRunState
  onPinArtifact?: (artifact: ReportingArtifact) => void
  pinnedArtifactIds?: ReadonlySet<string>
  pinningArtifactId?: string
  pinUnavailableArtifactIds?: ReadonlySet<string>
  canPinArtifacts?: boolean
  hasOlderMessages?: boolean
  isLoadingOlderMessages?: boolean
  olderMessagesFailed?: boolean
  onLoadOlderMessages?: () => void
  disabled?: boolean
  submitDisabled?: boolean
  expanded?: boolean
  initiallyExpanded?: boolean
  onExpandedChange?: (expanded: boolean) => void
  placeholder?: string
}

const activeRunStates: readonly ReportingChatRunState[] = [
  'submitting',
  'running',
  'refining',
]

const isActiveRun = (runState: ReportingChatRunState): boolean =>
  activeRunStates.includes(runState)

const ReportingAgentChat: React.FC<ReportingAgentChatProps> = ({
  messages = [],
  chartQuestionContext,
  artifactByRunId,
  value,
  onDraftChange,
  onSubmit,
  statusMessage = '',
  errorMessage,
  runState = 'idle',
  onPinArtifact,
  pinnedArtifactIds,
  pinningArtifactId,
  pinUnavailableArtifactIds,
  canPinArtifacts = true,
  hasOlderMessages = false,
  isLoadingOlderMessages = false,
  olderMessagesFailed = false,
  onLoadOlderMessages,
  disabled = false,
  submitDisabled = false,
  expanded,
  initiallyExpanded = true,
  onExpandedChange,
  placeholder,
}) => {
  const translate = useTranslate()
  const chatBoxRef = useRef<HTMLDivElement>(null)
  const loadingRef = useRef<HTMLDivElement>(null)
  const collapsedToggleRef = useRef<HTMLButtonElement>(null)
  const [internalValue, setInternalValue] = useState(value ?? '')
  const [internalExpanded, setInternalExpanded] = useState(initiallyExpanded)
  const composerValue = value ?? internalValue
  const isExpanded = expanded ?? internalExpanded
  const inputDisabled = disabled || isActiveRun(runState)
  const inputSubmitDisabled = inputDisabled || submitDisabled
  const resolvedPlaceholder =
    placeholder ?? translate('Ask for a report or refine the current one')
  const latestMessage = messages[messages.length - 1]
  const latestMessageScrollKey = latestMessage
    ? `${latestMessage.id}:${latestMessage.content ?? ''}`
    : undefined
  const finalAssistantIndexByRunId = useMemo(() => {
    const finalIndexes = new Map<string, number>()
    messages.forEach((message, index) => {
      if (message.role === 'assistant' && message.runId !== null) {
        finalIndexes.set(message.runId, index)
      }
    })
    return finalIndexes
  }, [messages])

  useEffect(() => {
    if (!chatBoxRef.current) return undefined
    const timeoutId = window.setTimeout(() => {
      chatBoxRef.current?.scrollTo?.({
        behavior: 'smooth',
        top: chatBoxRef.current.scrollHeight,
      })
    }, 100)
    return () => window.clearTimeout(timeoutId)
  }, [latestMessageScrollKey, runState])

  useEffect(() => {
    if (!isExpanded) collapsedToggleRef.current?.focus()
  }, [isExpanded])

  const loadingText = useLoopingTypingEffect([
    translate('Loading...'),
    translate('Working on it...'),
    translate('Just a moment...'),
    translate('Processing your request...'),
    translate('Thanks for your patience...'),
  ])

  const setDraft = (nextValue: string): void => {
    if (value === undefined) setInternalValue(nextValue)
    onDraftChange?.(nextValue)
  }

  const sendUserInput = (prompt: string): void => {
    const nextValue = prompt.trim()
    if (!nextValue || inputSubmitDisabled) return
    setDraft('')
    onSubmit?.(nextValue)
  }

  const setChatExpanded = (nextExpanded: boolean): void => {
    if (expanded === undefined) setInternalExpanded(nextExpanded)
    onExpandedChange?.(nextExpanded)
  }

  if (!isExpanded) {
    return (
      <ChatToggle
        ref={collapsedToggleRef}
        type="button"
        aria-label={translate('Open report assistant')}
        aria-expanded="false"
        onClick={(): void => setChatExpanded(true)}
      >
        {translate('Open')}
      </ChatToggle>
    )
  }

  return (
    <ChatWrapper data-testid="reporting-chat">
      {expanded !== undefined && (
        <ChatToggle
          type="button"
          aria-label={translate('Collapse report assistant')}
          aria-expanded="true"
          onClick={(): void => setChatExpanded(false)}
        >
          {translate('Collapse')}
        </ChatToggle>
      )}
      <ChatBox
        ref={chatBoxRef}
        aria-label={translate('Report assistant conversation')}
      >
        {chartQuestionContext && (
          <ChartQuestionContextCard context={chartQuestionContext} />
        )}
        {hasOlderMessages && onLoadOlderMessages && (
          <MessageHistoryControl>
            <Button
              type="button"
              variant="secondary"
              loading={isLoadingOlderMessages}
              onClick={onLoadOlderMessages}
            >
              {olderMessagesFailed
                ? translate('Retry')
                : translate('Load more')}
            </Button>
          </MessageHistoryControl>
        )}
        {messages.length === 0 &&
          !chartQuestionContext &&
          !isActiveRun(runState) && (
            <ReportingAgentChatEmptyState onPromptClick={sendUserInput} />
          )}
        {messages.map((message, index) => {
          const isFinalAssistantMessage =
            message.role === 'assistant' &&
            message.runId !== null &&
            finalAssistantIndexByRunId.get(message.runId) === index
          const artifact =
            isFinalAssistantMessage && message.runId !== null
              ? artifactByRunId?.get(message.runId)
              : undefined

          return (
            <React.Fragment key={message.id}>
              <MessageWrapper sender={message.role === 'user' ? 'user' : 'ai'}>
                <Timestamp>{message.createdAt}</Timestamp>
                <MessageBox sender={message.role === 'user' ? 'user' : 'ai'}>
                  {message.content !== null && (
                    <ChatMarkdown>{message.content}</ChatMarkdown>
                  )}
                </MessageBox>
              </MessageWrapper>
              {artifact && (
                <ReportingArtifactCard
                  artifact={artifact}
                  onPin={onPinArtifact}
                  isPinned={
                    pinnedArtifactIds?.has(artifact.id) ||
                    (artifact.liveReportCandidateId !== undefined &&
                      artifact.liveReportCandidateId !== null &&
                      pinnedArtifactIds?.has(artifact.liveReportCandidateId))
                  }
                  isPinning={pinningArtifactId === artifact.id}
                  isPinDisabled={
                    !canPinArtifacts ||
                    pinUnavailableArtifactIds?.has(artifact.id)
                  }
                />
              )}
            </React.Fragment>
          )
        })}
        {isActiveRun(runState) && (
          <LoadingText ref={loadingRef} aria-live="polite">
            {loadingText}
          </LoadingText>
        )}
      </ChatBox>
      {errorMessage && <ChatError role="alert">{errorMessage}</ChatError>}
      <InputBox>
        <ReportingAgentChatField
          value={composerValue}
          onChange={setDraft}
          onSubmit={(): void => sendUserInput(composerValue)}
          placeholder={resolvedPlaceholder}
          disabled={inputDisabled}
          submitDisabled={inputSubmitDisabled}
          loading={isActiveRun(runState)}
          focusOnMount={expanded !== undefined}
        />
      </InputBox>
      <ChatStatus aria-live="polite" aria-atomic="true">
        {statusMessage}
      </ChatStatus>
    </ChatWrapper>
  )
}

export { ReportingAgentChat }
export default ReportingAgentChat
